import * as Sentry from "@sentry/react";
import logo from "../../../assets/images/logo/Trellis_Logo.png";
import axios from "axios";
import React from "react";
import { Col, Row } from "react-bootstrap";
import { connect, type ConnectedProps } from "react-redux";
import { Trans, useTranslation, withTranslation, type WithTranslation } from "react-i18next";
import { logoutUser, setCurrentUser } from "../../../redux/reducers/userReducer";
import { getURLPrefix } from "../../../utils/getUrlPrefix";
import DOMPurify from "dompurify";
import type { RootState } from "../../../redux/store";
import ConfirmationModal from "../../core/blocks/ConfirmationModal";
import { css } from "../../../../styled-system/css";

const TermsOfUseTrigger = () => {
  const { t } = useTranslation("common");
  return (
    <span
      className={css({
        cursor: "pointer",
        paddingX: "xsmall",
        color: "trellisPalette.0",
        fontWeight: "bold",
        whiteSpace: "nowrap",
      })}
    >
      {t(($) => $.common["Terms of Use"])}
    </span>
  );
};

const URL_PREFIX = getURLPrefix();

type Props = {
  justTerms?: string;
  closeTerms?: () => void;
} & PropsFromRedux &
  WithTranslation<["common"]>;

interface State {
  agreement: string;
  tosVersion: string;
  docCreatedTime: string;
  shortForm: boolean;
  justTerms: boolean;
}

class TermsOfService extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      agreement: "",
      tosVersion: "",
      docCreatedTime: "",
      shortForm: true,
      justTerms: this.props.justTerms !== undefined,
    };
  }

  componentDidMount() {
    this.fetchAgreement();
  }

  goToTop() {
    setTimeout(function () {
      document.getElementById("topOfDoc")?.scrollIntoView();
    }, 50);
  }

  fetchAgreement() {
    const AuthStr = "Bearer ".concat(this.props.auth.tokens.access_token);
    axios
      .get(URL_PREFIX + "/api/documents/?type=tos", {
        params: {},
        headers: this.props.justTerms === "showTerms" ? {} : { Authorization: AuthStr },
      })
      .then((res) => {
        this.setState(
          {
            agreement: res.data.html_text,
            tosVersion: res.data.version,
            docCreatedTime: res.data.created_datetime,
          },
          () => {
            if (this.props.justTerms === "showCancellation") {
              setTimeout(function () {
                document.querySelector("#suspension")?.scrollIntoView();
              }, 50);
            }
          },
        );
      })
      .catch(Sentry.captureException);
  }

  onAgreeing() {
    const AuthStr = "Bearer ".concat(this.props.auth.tokens.access_token);
    const data = {
      tos_version: this.state.tosVersion,
    };
    const reduxData = {
      latest_tos_agreement_status: "agreed",
    };
    axios
      .put(URL_PREFIX + "/api/userprofile/", data, {
        params: {},
        headers: { Authorization: AuthStr },
      })
      .then((res) => {
        if (res.data === "Updated") {
          this.props.setCurrentUser(reduxData);
        }
      })
      .catch(() => {});
  }

  render() {
    const { t, i18n } = this.props;
    const docDate = this.state.docCreatedTime ? new Date(this.state.docCreatedTime) : null;
    const formattedDate =
      docDate && !isNaN(docDate.getTime()) ?
        new Intl.DateTimeFormat(i18n.language, {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        }).format(docDate)
      : "";

    return (
      <ConfirmationModal
        inlineModal={true}
        modalButtonText={<TermsOfUseTrigger />}
        onConfirm={() => {
          this.state.justTerms ? this.props.closeTerms?.() : this.onAgreeing();
        }}
        confirmButtonText={this.state.justTerms ? t(($) => $.common.actions.Close) : t(($) => $.common.tos["I Agree"])}
        onCancel={() => {
          this.state.justTerms ? this.props.closeTerms?.() : this.props.logoutUser();
        }}
        hideCancelButton={this.state.justTerms}
        inlineButtonContainerStyles={{}}
      >
        <div className={css({ maxHeight: "70vh", overflowY: "scroll" })}>
          <img
            src={logo}
            alt={t(($) => $.common.tos.Trellis)}
            className={css({ width: "300px", paddingBottom: "xlarge" })}
            id="topOfDoc"
          />
          {this.state.shortForm && !this.state.justTerms ?
            <div>
              <Row>
                <Col
                  style={{
                    textAlign: "center",
                    color: "#393939",
                    fontSize: "34px",
                    fontWeight: 700,
                    marginTop: "3rem",
                  }}
                  md={{ span: 10, offset: 1 }}
                >
                  {t(($) => $.common.tos["Thank you for using Trellis!"])}
                </Col>
              </Row>
              <Row>
                <Col
                  style={{
                    textAlign: "center",
                    color: "#393939",
                    fontSize: "20px",
                    fontWeight: 500,
                    marginTop: "2rem",
                    marginBottom: "3rem",
                  }}
                  md={{ span: 10, offset: 1 }}
                >
                  <Trans
                    t={t}
                    i18nKey={($) =>
                      $.common.tos[
                        "Please read and acknowledge our updated <termsLink>terms of service</termsLink> as of {date}."
                      ]
                    }
                    values={{ date: formattedDate }}
                    components={{
                      termsLink: (
                        <span
                          style={{
                            color: "#D91266",
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            this.setState({ shortForm: false }, () => {
                              this.goToTop();
                            });
                          }}
                        />
                      ),
                    }}
                  />
                </Col>
              </Row>
            </div>
          : <>
              <p
                style={{
                  textAlign: "left",
                  fontStyle: "italic",
                  fontSize: "15px",
                  color: "red",
                }}
                className="text-center"
              >
                {t(
                  ($) =>
                    $.common.tos[
                      "Thank you for using Trellis. Please read and acknowledge our updated terms of service as of {date}."
                    ],
                  { date: formattedDate },
                )}
              </p>
              <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(this.state.agreement) }} />
            </>
          }
        </div>
      </ConfirmationModal>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  auth: state.auth,
});

const mapDispatchToProps = {
  setCurrentUser,
  logoutUser,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(withTranslation("common")(TermsOfService));
